import React, { useEffect, useRef } from 'react';
import { Button, Input, Modal, Text } from '@fragments';
import { IconArrowRight, IconCheck, IconSparkles } from '@tabler/icons-react';
import { ImageLibrary } from '../../features/ArtEditor';
import useImageChoice from './hooks/useImageChoice';
import OwnPhoto from './components/OwnPhoto';
import PlaceList from './components/PlaceList';
import PlacePreview from './components/PlacePreview';
import { choiceImageId, hasImage, openImageQuestion, suggestionImage } from './format';
import './styles.scss';

// What the footer says: how many places are left (2.12.0: every place needs an image), then
// the generations and the ceiling the creator confirms (the estimate, or a quote that came
// back above it), or why the answer did not go through.
function Summary({ t, choice }) {
  const { generating, estimate, quote, error, notice, pending } = choice;
  if (pending.length) {
    return <div className="studio-images__summary" aria-live="polite">
      <Text.label contrast={9}>{t('imagesLeft', { count: pending.length })}</Text.label>
      <Text.caption contrast={6}>{t('imagesLeftHint')}</Text.caption>
    </div>;
  }
  const credits = quote.status === 'ready' ? quote.value.quoted_units : estimate;
  const status = error ? <Text.caption contrast={8} role="alert">{t(`error_${error}`, { defaultValue: t('error_generic') })}</Text.caption>
    : quote.status === 'error' ? <Text.caption contrast={8} role="alert">{t(`error_${quote.error}`, { defaultValue: t('error_generic') })}</Text.caption>
    : quote.status === 'loading' ? <Text.caption contrast={6} loading>{t('imagesQuoting')}</Text.caption>
    : notice === 'requoted' && quote.status === 'ready' ? <Text.caption contrast={7} role="status">{t('imagesRequoted')}</Text.caption>
    : generating.length ? <Text.caption contrast={7}>{t('imagesConfirmHint')}</Text.caption>
    : null;
  return <div className="studio-images__summary" aria-live="polite">
    <Text.label contrast={9}>{generating.length ? t('imagesTotalAi', { count: generating.length, credits }) : t('imagesTotalNone')}</Text.label>
    {status}
  </div>;
}

// The selected place: which of the places it is, its subject, the creator's own photo first
// (the recommended source, with the tip for it and the creator's images the AI found), then
// the rest of their images and the community's; its page with the choice in it, the
// generation (the mode and its ceiling; once chosen, the subject to rewrite, which only a
// generation uses) and, when the place already shows an image, keeping it. While
// generating, the head shows the subject that will be sent.
function Place({ studio: s, choice }) {
  const { t } = s;
  const { place, places, choices, subjects } = choice;
  const current = choices[place.slot_id];
  const selectedId = choiceImageId(current);
  const suggested = (place.suggested || []).filter(item => item.url).map(suggestionImage);
  const offered = choice.offered(place);
  const generating = current?.source === 'generate';
  const keeping = current?.source === 'keep';
  const placement = t(`imagesPlacement_${place.placement}`);
  const subject = generating ? subjects[place.slot_id]?.trim() || place.subject : place.subject;
  const number = places.findIndex(item => item.slot_id === place.slot_id) + 1;
  // Another place opens on its own photo, at the top of the images.
  const sources = useRef(null);
  useEffect(() => { if (sources.current) sources.current.scrollTop = 0; }, [place.slot_id]);
  return <>
    <section className="studio-images__main" aria-labelledby="studio-images-place">
      <header className="studio-images__head" aria-live="polite">
        <Text.subheading contrast={6}>{`${t('imagesStep', { number, total: places.length })} · ${t('imagesPage', { number: place.page })} · ${placement}`}</Text.subheading>
        <Text.h6 id="studio-images-place" weight={500} contrast={9}>{subject}</Text.h6>
      </header>
      <div ref={sources} className="studio-images__sources">
        <OwnPhoto t={t} place={place} suggested={suggested} selectedId={selectedId} onPick={choice.pickImage} onUpload={choice.upload} />
        {s.art.images && <section className="studio-images__others" aria-label={t('imagesOthers')}>
          <div className="studio-images__section-head">
            <Text.subheading contrast={6}>{t('imagesOthers')}</Text.subheading>
            <Text.caption contrast={6}>{t('imagesOthersHint')}</Text.caption>
          </div>
          <ImageLibrary source={s.art.images} selectedId={selectedId ?? null} onPick={choice.pickImage} added={choice.added} columns={4} pickLabel={t('imagesPick')} />
        </section>}
      </div>
    </section>
    <aside className="studio-images__side" aria-label={t('imagesPlaceOptions')}>
      {choice.draft && <div className="studio-images__preview"><PlacePreview t={t} draft={choice.draft} place={place} choice={current} /></div>}
      {offered && <section className={`studio-images__generate ${generating ? 'is-chosen' : ''}`} aria-label={t('imagesGenerate')}>
        <div className="studio-images__generate-head">
          <Text.label contrast={9} left={<IconSparkles />}>{t('imagesGenerate')}</Text.label>
          <Text.caption contrast={6}>{t('imagesGenerateCost', { mode: t(`imagesMode_${place.mode}`), credits: place.generation_credits })}</Text.caption>
        </div>
        <Button fullWidth variant="rounded" color="ai" preventLoading startIcon={generating ? <IconCheck /> : <IconSparkles />} aria-pressed={generating}
          onClick={generating ? choice.clear : choice.generate}>{t(generating ? 'imagesGenerating' : 'imagesGenerate')}</Button>
        {generating && <Input multiline rows={3} maxLength={300} value={subjects[place.slot_id] ?? place.subject} onInput={choice.setSubject}
          label={t('imagesSubject')} aria-label={t('imagesSubject')} />}
      </section>}
      {hasImage(place) && <Button className={`studio-images__keep ${keeping ? 'is-chosen' : ''}`} fullWidth variant="rounded" color="overlay" preventLoading aria-pressed={keeping}
        startIcon={keeping ? <IconCheck /> : null} onClick={keeping ? choice.clear : choice.keep}>{t('imagesKeep')}</Button>}
    </aside>
  </>;
}

function ImageChoice({ studio: s }) {
  const { t } = s;
  const choice = useImageChoice(s);
  const { places, place, pending, quote, generating, submitting } = choice;
  if (!place) return null;
  const ai = generating.length > 0;
  const quoting = quote.status === 'loading';
  const done = places.length - pending.length;
  // While places are left: "Próxima imagem" goes to the next one; with only this one left, the
  // answer waits for it. With a generation, one "Confirmar e gerar" quotes and answers.
  const others = pending.some(item => item.slot_id !== place.slot_id);
  const primary = others
    ? <Button variant="rounded" color="inverse" preventLoading endIcon={<IconArrowRight />} onClick={choice.next}>{t('imagesNext')}</Button>
    : <Button variant="rounded" color={ai ? 'ai' : 'inverse'} preventLoading loading={submitting || quoting} disabled={submitting || quoting || pending.length > 0}
      startIcon={ai ? <IconSparkles /> : null} onClick={choice.confirm}>{t(ai ? 'imagesConfirmGenerate' : 'imagesApply')}</Button>;
  const footer = <div className="studio-images__footer">
    <Summary t={t} choice={choice} />
    <div className="studio-images__actions">
      <Button variant="text" color="overlay" preventLoading disabled={submitting} onClick={choice.close}>{t('cancel')}</Button>
      {primary}
    </div>
  </div>;
  return <Modal open size={1180} closeLabel={t('close')} onClose={choice.close} className="studio-dialog studio-images-dialog" footer={footer}
    title={t('imagesTitle')} subtitle={t('imagesSubtitle', { count: places.length })}>
    <div className="studio-images">
      <nav className="studio-images__places" aria-label={t('imagesPlaces', { count: places.length })}>
        <div className="studio-images__progress">
          <Text.label contrast={9}>{t('imagesProgress', { done, total: places.length })}</Text.label>
          <span className="studio-images__bar" role="progressbar" aria-valuemin={0} aria-valuemax={places.length} aria-valuenow={done}
            aria-label={t('imagesProgress', { done, total: places.length })}><span style={{ width: `${(done / places.length) * 100}%` }} /></span>
        </div>
        <PlaceList t={t} places={places} choices={choice.choices} selectedId={choice.selectedId} onSelect={choice.select} />
      </nav>
      <Place studio={s} choice={choice} />
    </div>
  </Modal>;
}

// "Escolher imagens" (contract 2.9.0; 2.12.0 asks for every place and leads with the
// creator's own photo): the run's image question, place by place. Mounted with
// `studio.modal.type === 'images'`; once the question is no longer open (answered,
// cancelled, expired) it closes itself. Each version of the question starts its own choice.
export default function ImageChoiceDialog({ studio: s }) {
  const action = openImageQuestion(s.run);
  const { setModal } = s;
  useEffect(() => { if (!action) setModal(null); }, [action, setModal]);
  if (!action || !action.images?.length) return null;
  return <ImageChoice key={`${action.id}:${action.version}`} studio={s} />;
}
